/**
 * เสียง — ไฟล์ที่ bundle มาก่อน, ไม่มีค่อยใช้ TTS
 *
 * ทำไมไม่พึ่ง Web Speech อย่างเดียว: Windows ต้องติดตั้ง Japanese language pack
 * ไม่งั้นเงียบสนิท (ดู DATA-SOURCES.md §5)
 */

const BASE = import.meta.env.BASE_URL

const cache = new Map<string, HTMLAudioElement>()
/**
 * ไฟล์ที่กำลังเล่นอยู่ — ต้องหยุดก่อนเล่นตัวใหม่
 *
 * แต่ละการ์ดมี element ของตัวเอง เปลี่ยนการ์ดเร็ว ๆ จึงเล่นทับกันได้ (วัดได้ 3 เสียง
 * พร้อมกันตอนกด Space รัว) ต่างจากฝั่ง TTS ที่ speechSynthesis.cancel() ตัดให้เอง
 */
let nowPlaying: HTMLAudioElement | null = null

function stopAll(): void {
  if (nowPlaying) {
    nowPlaying.pause()
    nowPlaying.currentTime = 0
    nowPlaying = null
  }
  if ('speechSynthesis' in window) speechSynthesis.cancel()
}
let ttsVoice: SpeechSynthesisVoice | null = null
let ttsChecked = false

function pickVoice(): SpeechSynthesisVoice | null {
  if (ttsChecked) return ttsVoice
  if (!('speechSynthesis' in window)) {
    ttsChecked = true
    return null
  }
  const voices = speechSynthesis.getVoices()
  // getVoices() คืน [] จนกว่า voiceschanged จะยิง — อย่าเพิ่งปิดประตู
  if (voices.length === 0) return null
  ttsVoice = voices.find((v) => v.lang.replace('_', '-').startsWith('ja')) ?? null
  ttsChecked = true
  return ttsVoice
}

if ('speechSynthesis' in window) {
  speechSynthesis.addEventListener('voiceschanged', () => {
    ttsChecked = false
    pickVoice()
  })
}

/** มีเสียงญี่ปุ่นให้ใช้ไหม — โชว์คำเตือนใน settings ถ้าไม่มี */
export function hasJapaneseVoice(): boolean {
  return pickVoice() !== null
}

function speak(text: string): boolean {
  const voice = pickVoice()
  if (!voice) return false
  stopAll()
  const u = new SpeechSynthesisUtterance(text)
  u.voice = voice
  u.lang = 'ja-JP'
  u.rate = 0.85
  speechSynthesis.speak(u)
  return true
}

/** opus ก่อนเพราะเล็กกว่า ~10 เท่า · wav คือผลดิบจาก npm run audio:kana */
const EXTS = ['opus', 'wav'] as const

function urlFor(file: string, ext: string): string {
  return `${BASE}audio/kana/${encodeURIComponent(file)}.${ext}`
}

/** จำไว้ว่าไฟล์ไหนไม่มี จะได้ไม่ยิง 404 ซ้ำทุกครั้งที่เจอการ์ดเดิม */
const missing = new Set<string>()

/**
 * เล่นเสียงของ kana/คำ
 * @param file ชื่อไฟล์ (ไม่มีนามสกุล) ใน /audio/kana/
 * @param fallbackText ข้อความให้ TTS อ่านถ้าไม่มีไฟล์
 */
export async function playKana(file: string, fallbackText: string): Promise<boolean> {
  for (const ext of EXTS) {
    const url = urlFor(file, ext)
    if (missing.has(url)) continue

    let el = cache.get(url)
    if (!el) {
      el = new Audio(url)
      el.preload = 'auto'
      cache.set(url, el)
    }

    try {
      stopAll()
      el.currentTime = 0
      nowPlaying = el
      await el.play()
      return true
    } catch (err) {
      /*
       * มีสองกรณีที่ล้มเหลวโดยที่ไฟล์ไม่ได้หายไปไหน — ขึ้นบัญชีไปจะทำให้ตัวนั้น
       * เงียบไปทั้ง session แม้กด 🔊 เองก็ตาม:
       *
       *   NotAllowed  เบราว์เซอร์ห้ามเล่นก่อนผู้ใช้แตะอะไรในหน้านั้น — การ์ดใบแรก
       *               หลังเปิดแอปโดนเป็นปกติ
       *   Abort       ถูก pause ระหว่างกำลังเริ่มเล่น เพราะกดข้อถัดไปเร็วกว่าเสียง
       */
      const name = err instanceof DOMException ? err.name : ''
      if (name === 'NotAllowedError' || name === 'AbortError') return false
      missing.add(url)
      cache.delete(url)
    }
  }
  // ไม่มีไฟล์เลย → TTS ของระบบ (Windows อาจไม่มีเสียงญี่ปุ่น ดู DATA-SOURCES.md §5)
  return speak(fallbackText)
}

/**
 * อ่านข้อความด้วยเสียงของระบบ — ใช้กับคำศัพท์และประโยคที่ไม่มีไฟล์เสียง
 *
 * ทำไมไม่อัดไฟล์ไว้เหมือน kana: คำ 3,000 คำกับประโยคอีก 3,000 ประโยคคือ 38 MB
 * ส่วนเสียงระบบให้ผลเท่ากันบนเครื่องที่ลงเสียงญี่ปุ่นไว้แล้ว — เสียงเดียวกับที่ใช้
 * generate ไฟล์ kana นั่นเอง (Ayumi/Haruka/Ichiro/Sayaka)
 *
 * ทำไมไม่ต่อเสียง kana ทีละพยางค์เอง: ญี่ปุ่นใช้ระดับเสียงแยกความหมาย
 * (箸 HAshi ตะเกียบ ≠ 橋 haSHI สะพาน) และเสียงหายจริงในคำพูด (です → des)
 * ต่อพยางค์แล้วได้เสียงที่ไม่มีใครพูดแบบนั้น
 */
export function speakText(text: string): boolean {
  return speak(text)
}

/** โหลดล่วงหน้าไว้ ไม่ให้หน่วงตอนกดฟัง */
export function prefetch(files: string[]): void {
  for (const f of files.slice(0, 30)) {
    const url = urlFor(f, EXTS[0])
    if (cache.has(url) || missing.has(url)) continue
    const el = new Audio(url)
    el.preload = 'auto'
    cache.set(url, el)
  }
}
